import React, { useState } from "react";
import { styled, Theme, CSSObject } from "@mui/material/styles";
import { makeStyles } from "@mui/styles";
import MuiDrawer from "@mui/material/Drawer";
import {
  Typography,
  Divider,
  Box,
  Avatar,
  Button,
  Popover,
  IconButton,
} from "@mui/material";
import TreeView from "../../components/TreeView";
import AppProject from "./AddProject";
import useAuth from "../../hooks/useAuth";
import { User } from "../../declarations/auth";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  paper: {
    backgroundColor: `${theme.palette.background.secondaryLight} !important`,
    border: "0 !important",
  },
}));

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": closedMixin(theme),
  }),
}));

const Profile: React.FC<{
  user: User;
  onLogout: any;
}> = ({ user, onLogout }) => {
  const [anchorEl, setAnchorEl] = useState<any>(null);
  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = () => {
    onLogout();
  };
  return (
    <Box>
      <IconButton
        sx={{ padding: 0.5 }}
        onClick={handleClick}
        id="demo-customized-button"
        aria-controls={open ? "demo-customized-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        aria-label="Profile Avatar"
      >
        <Avatar
          alt={user.name}
          src={user.picture}
          sx={{ width: 30, height: 30 }}
        />
      </IconButton>
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box textAlign="center" minWidth={250}>
          <Box p={2}>
            <Box display="flex" justifyContent="center" mb={2}>
              <Avatar
                alt={user.name}
                src={user.picture}
                sx={{ width: 50, height: 50 }}
              />
            </Box>
            <Typography variant="h5" color="primaryHighlight.main" mb={1}>
              {user.name}
            </Typography>
            <Typography variant="subtitle1" color="primary" mb={1}>
              {user.email}
            </Typography>
          </Box>
          {user.email != "guest@gmail.com" && (
            <>
              <Divider />
              <Box p={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  sx={{
                    // padding: "10px 21px",
                    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.05)",
                    textTransform: "unset !important",
                  }}
                  onClick={handleLogout}
                  aria-label="Logout"
                >
                  Logout
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Popover>
    </Box>
  );
};

const SideBar = () => {
  const classes = useStyles();
  const { user, logout } = useAuth();

  return (
    <Drawer variant="permanent" open={true} classes={classes}>
      <Box px={2} pt={2} pb={1.25}>
        <Box display="flex" alignItems="center" gap="12px" mb={1.25}>
          {user && <Profile user={user} onLogout={logout} />}
          <Box>
            <Typography variant="h5" color="primaryHighlight.main">
              Dopple Studio
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mt={0.5}
        >
          <Typography variant="subtitle2" color="primary">
            PROJECTS
          </Typography>
          <AppProject />
        </Box>
      </Box>
      <TreeView />
    </Drawer>
  );
};

export default SideBar;
